import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, FlatList } from 'react-native';
import { InsetModal as Modal } from '../InsetModal';
import { Ionicons } from '@expo/vector-icons';
import { Input } from '../design-system';
import { colors, spacing, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { VariableGroup } from './flow-types';
import { useThemedStyles } from '../../hooks/useThemedStyles';

interface VariablePickerProps {
  visible: boolean;
  onClose: () => void;
  groups: VariableGroup[];
  onSelect: (path: string) => void;
}

/** Modal listing every {{variable}} available for interpolation, grouped (Contact, This
 * Flow's Collected Values, ...). Tapping a fixed entry inserts it immediately; the one
 * free-text group (Contact's custom fields) shows an inline suffix input instead, since
 * customFields is unstructured data with no fixed key list. */
export const VariablePicker: React.FC<VariablePickerProps> = ({ visible, onClose, groups, onSelect }) => {
  const styles = useThemedStyles(getStyles);
  const [customFieldDraft, setCustomFieldDraft] = useState('');

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={styles.content}>
          <View style={styles.header}>
            <Text style={styles.title}>Insert Value</Text>
            <TouchableOpacity onPress={onClose} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
              <Ionicons name="close" size={24} color={colors.textDark} />
            </TouchableOpacity>
          </View>
          <FlatList
            data={groups}
            keyExtractor={(group) => group.key}
            renderItem={({ item: group }) => (
              <View style={styles.group}>
                <Text style={styles.groupLabel}>{group.label}</Text>
                {group.entries.map((entry) => (
                  <TouchableOpacity
                    key={entry.path}
                    style={styles.option}
                    onPress={() => {
                      onSelect(entry.path);
                      onClose();
                    }}
                  >
                    <Text style={styles.optionLabel}>{entry.label}</Text>
                    <Text style={styles.optionPath}>{'{{' + entry.path + '}}'}</Text>
                  </TouchableOpacity>
                ))}
                {group.freeTextPrefix && (
                  <View style={styles.customFieldRow}>
                    <View style={{ flex: 1 }}>
                      <Input
                        placeholder="Custom field name, e.g. birthday"
                        value={customFieldDraft}
                        onChangeText={setCustomFieldDraft}
                        size="sm"
                        autoCapitalize="none"
                      />
                    </View>
                    <TouchableOpacity
                      style={[styles.insertButton, !customFieldDraft.trim() && styles.insertButtonDisabled]}
                      disabled={!customFieldDraft.trim()}
                      onPress={() => {
                        onSelect(`${group.freeTextPrefix}${customFieldDraft.trim()}`);
                        setCustomFieldDraft('');
                        onClose();
                      }}
                    >
                      <Text style={styles.insertButtonText}>Insert</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>
            )}
            ListEmptyComponent={<View style={styles.emptyContainer}><Text style={styles.emptyText}>No values available yet</Text></View>}
          />
        </View>
      </View>
    </Modal>
  );
};

const getStyles = () => StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0, 0, 0, 0.5)', justifyContent: 'flex-end' },
  content: {
    backgroundColor: colors.background.surface,
    borderTopLeftRadius: designTokens.borderRadius.lg,
    borderTopRightRadius: designTokens.borderRadius.lg,
    maxHeight: '80%',
    paddingBottom: spacing.lg,
  },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: spacing.lg, borderBottomWidth: 1, borderBottomColor: colors.border },
  title: { fontSize: typography.fontSize.xl, fontWeight: '700', color: colors.textDark, fontFamily: typography.fontFamily.bold },
  group: { paddingTop: spacing.md },
  groupLabel: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.semibold, color: colors.textLight, textTransform: 'uppercase', paddingHorizontal: spacing.lg, marginBottom: spacing.xs },
  option: { paddingHorizontal: spacing.lg, paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: `${String(colors.border)}40` },
  optionLabel: { fontSize: typography.fontSize.base, color: colors.textDark, fontFamily: typography.fontFamily.medium },
  optionPath: { fontSize: typography.fontSize.xs, color: colors.textLight, fontFamily: typography.fontFamily.primary, marginTop: 2 },
  customFieldRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingHorizontal: spacing.lg, paddingVertical: spacing.sm },
  insertButton: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: designTokens.borderRadius.sm, backgroundColor: colors.primary },
  insertButtonDisabled: { backgroundColor: colors.border },
  insertButtonText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.white },
  emptyContainer: { padding: spacing.xl, alignItems: 'center', justifyContent: 'center' },
  emptyText: { fontSize: typography.fontSize.base, color: colors.textLight, fontFamily: typography.fontFamily.primary },
});
